import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api, onSignedOut, refreshAccessToken, setAccessToken } from './client.js';

import type { LoginResponse, Me, SsoProvider } from '@baseline/shared';
import type { ReactNode } from 'react';

// Who is signed in. On load the refresh cookie brings the session back; a failed refresh
// shows the login page (plan Phase 10).

type SessionState = { status: 'loading' } | { status: 'out' } | { status: 'in'; user: Me };

interface Session {
  state: SessionState;
  login(email: string, password: string): Promise<void>;
  sso(provider: SsoProvider): Promise<void>;
  logout(): Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

// A hint, not a credential: whether this browser signed in before. Without it the app skips
// the refresh call, so the login page makes no request that is bound to fail.
const HINT_KEY = 'baseline.signedIn';

function hint(value?: boolean): boolean {
  try {
    if (value === true) localStorage.setItem(HINT_KEY, '1');
    else if (value === false) localStorage.removeItem(HINT_KEY);
    return localStorage.getItem(HINT_KEY) === '1';
  } catch {
    // Storage blocked: always try the refresh.
    return true;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const queryClient = useQueryClient();

  useEffect(() => {
    let live = true;
    void (async () => {
      const ok = hint() && (await refreshAccessToken());
      if (!live) return;
      if (!ok) {
        hint(false);
        setState({ status: 'out' });
        return;
      }
      try {
        const user = await api<Me>('/auth/me');
        if (live) setState({ status: 'in', user });
      } catch {
        if (live) setState({ status: 'out' });
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  useEffect(
    () =>
      onSignedOut(() => {
        hint(false);
        queryClient.clear();
        setState({ status: 'out' });
      }),
    [queryClient],
  );

  const signedIn = useCallback((res: LoginResponse) => {
    setAccessToken(res.accessToken);
    hint(true);
    setState({ status: 'in', user: res.user });
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      signedIn(
        await api<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } }),
      );
    },
    [signedIn],
  );

  const sso = useCallback(
    async (provider: SsoProvider) => {
      signedIn(await api<LoginResponse>(`/auth/sso/${provider}`, { method: 'POST' }));
    },
    [signedIn],
  );

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      hint(false);
      queryClient.clear();
      setState({ status: 'out' });
    }
  }, [queryClient]);

  const value = useMemo(() => ({ state, login, sso, logout }), [state, login, sso, logout]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession needs a SessionProvider.');
  return session;
}

/** The signed-in user. Only call it under the app shell, where a user is certain. */
export function useMe(): Me {
  const { state } = useSession();
  if (state.status !== 'in') throw new Error('No user is signed in.');
  return state.user;
}
